import { useCallback, useMemo } from 'react';
// @ts-expect-error untyped JS service
import { usePeriodById } from '../services/period.service';
// @ts-expect-error untyped JS service
import { useGetWorkflowById } from '../services/workflow.services';
// @ts-expect-error untyped JS service
import { useGetJurisdictions } from '../services/jurisdiction.service';
// @ts-expect-error untyped JS service
import { useUserGroupsCurrentUserAdminState } from '../services/user.service';
import {
  // @ts-expect-error untyped JS service
  useGetDetailedExtractionStatsByPeriodId,
  // @ts-expect-error untyped JS service
  useGetDetailedTransformationStatsByPeriodId
} from '../services/funds.service';
// @ts-expect-error untyped JS hook
import { useSignalRListener } from './useSignalR';
// @ts-expect-error untyped JS const map
import { SIGNALR_LISTENERS } from '../config/signalr';
// @ts-expect-error untyped JS module
import { queryClient } from '../config/queryClient';
import {
  // @ts-expect-error untyped JS module
  getStatus,
  // @ts-expect-error untyped JS module
  getPlaceholder,
  // @ts-expect-error untyped JS module
  getStatusForPFTrans,
  // @ts-expect-error untyped JS module
  getPlaceholderForPFTrans,
  // @ts-expect-error untyped JS module
  getStatusForPFFinal,
  // @ts-expect-error untyped JS module
  getPlaceholderForPFFinal,
  // @ts-expect-error untyped JS module
  getStatusForCustomReportType,
  // @ts-expect-error untyped JS module
  getPlaceholderForCustomReportType,
  // @ts-expect-error untyped JS module
  getStatusForConversionReportType,
  // @ts-expect-error untyped JS module
  getPlaceholderForConversionReportType,
  // @ts-expect-error untyped JS module
  statusKeys
} from '../pages/Project/Quarters/data';
// @ts-expect-error untyped JS const map
import { REPORT_TYPES, REPORT_NAMINGS } from '../consts/reportTypes';
import { FEATURE_FLAG_NAMES, useFeatureFlag } from './useFeatureFlag';

export type WorkflowStageStatus = 'done' | 'current' | 'upcoming';
export type WorkflowStageTone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

// Declarative "shortcut" action attached to a stage — e.g. copy the
// previous period's jurisdictions in one click. The stepper (or its host)
// owns the mutation itself; the hook only declares what shortcuts each
// stage supports, not how to run them.
export type WorkflowStageActionKind =
  'copy-jurisdictions' | 'automate-extraction' | 'automate-transformation';

export interface WorkflowStageAction {
  kind: WorkflowStageActionKind;
  label: string;
}

export interface WorkflowStage {
  key: string;
  label: string;
  path: string;
  status: WorkflowStageStatus;
  tone?: WorkflowStageTone;
  subLabel?: string;
  action?: WorkflowStageAction;
  /** When true, the StepItem renders a spinner next to the sub-label. */
  isLoading?: boolean;
}

export const WORKFLOW_STAGE_KEYS = {
  // `DASHBOARD` is a virtual leading stage that only appears for automation /
  // from-template periods. It links back to the project's Quarters page so
  // users can leave a period without hunting for a back button.
  DASHBOARD: 'dashboard',
  AIFS: 'aifs',
  JURISDICTIONS: 'jurisdictions',
  SOURCE_FILES: 'source_files',
  EXTRACTION: 'extraction',
  TRANSFORMATION: 'transformation',
  LOOKUP_TABLES: 'lookup_tables',
  OUTPUT_DATA: 'output_data',
  REPORT: 'report',
  APPROVAL: 'approval',
  SUBMISSION: 'submission'
} as const;

export type WorkflowStageKey = (typeof WORKFLOW_STAGE_KEYS)[keyof typeof WORKFLOW_STAGE_KEYS];

export interface UseWorkflowStagesArgs {
  workflowId?: string;
  periodId?: string;
}

// Stage ordering. Conditional inclusions handled inside `useWorkflowStages`:
//   - `DASHBOARD` only renders for automation / from-template periods.
//   - `LOOKUP_TABLES` only renders when the `lookup_table` company flag is on.
const STAGE_ORDER: WorkflowStageKey[] = [
  WORKFLOW_STAGE_KEYS.DASHBOARD,
  WORKFLOW_STAGE_KEYS.AIFS,
  WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  WORKFLOW_STAGE_KEYS.EXTRACTION,
  WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  WORKFLOW_STAGE_KEYS.LOOKUP_TABLES,
  WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  WORKFLOW_STAGE_KEYS.REPORT,
  WORKFLOW_STAGE_KEYS.APPROVAL,
  WORKFLOW_STAGE_KEYS.SUBMISSION
];

// Pluralize a noun by appending "s" unless it already looks plural
// (e.g. "Fund Info" stays "Fund Info").
const pluralize = (noun: string): string => (/info|s$/i.test(noun.trim()) ? noun : `${noun}s`);

/**
 * Stage labels are mostly fixed but AIFs / Jurisdictions vary by report type.
 *  - Annex IV / Conversion: "AIFs", "Jurisdictions"
 *  - Form PF: "Funds", "Fund Info"
 *  - Custom: "Assets", "Jurisdictions"
 */
const buildStageLabels = (reportType?: string): Record<WorkflowStageKey, string> => {
  const fundSingular = (REPORT_NAMINGS?.FUND?.[reportType ?? ''] as string) ?? 'AIF';
  const jurisSingular =
    (REPORT_NAMINGS?.JURISDICTION?.[reportType ?? ''] as string) ?? 'Jurisdiction';
  return {
    [WORKFLOW_STAGE_KEYS.DASHBOARD]: 'Dashboard',
    [WORKFLOW_STAGE_KEYS.AIFS]: pluralize(fundSingular),
    [WORKFLOW_STAGE_KEYS.JURISDICTIONS]: pluralize(jurisSingular),
    [WORKFLOW_STAGE_KEYS.SOURCE_FILES]: 'Source Files',
    [WORKFLOW_STAGE_KEYS.EXTRACTION]: 'Extraction',
    [WORKFLOW_STAGE_KEYS.TRANSFORMATION]: 'Transformation',
    [WORKFLOW_STAGE_KEYS.LOOKUP_TABLES]: 'Lookup',
    [WORKFLOW_STAGE_KEYS.OUTPUT_DATA]: 'Output Data',
    [WORKFLOW_STAGE_KEYS.REPORT]: 'Report',
    [WORKFLOW_STAGE_KEYS.APPROVAL]: 'Approval',
    [WORKFLOW_STAGE_KEYS.SUBMISSION]: 'Submission'
  };
};

// Maps Quarters/data.jsx statusKeys to our stepper stages. Note: data.jsx's
// statusKeys.REPORT means "generate output data / report" — that work happens
// on the OutputTables page, so it maps to OUTPUT_DATA. The dedicated REPORT
// stage (viewing the actual report) has no separate status key — once the
// workflow advances past data.jsx's REPORT to APPROVAL, our REPORT stage
// auto-becomes "done" via the linear progression model.
const STATUS_TO_STAGE: Record<string, WorkflowStageKey> = {
  [statusKeys.AIF]: WORKFLOW_STAGE_KEYS.AIFS,
  [statusKeys.JURISDICTION]: WORKFLOW_STAGE_KEYS.JURISDICTIONS,
  [statusKeys.FILES]: WORKFLOW_STAGE_KEYS.SOURCE_FILES,
  [statusKeys.EXTRACTION]: WORKFLOW_STAGE_KEYS.EXTRACTION,
  [statusKeys.EXTRACTION_IN_PROGRESS]: WORKFLOW_STAGE_KEYS.EXTRACTION,
  [statusKeys.TRANSFORMATION]: WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  [statusKeys.TRANSFORMATION_IN_PROGRESS]: WORKFLOW_STAGE_KEYS.TRANSFORMATION,
  [statusKeys.REPORT]: WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
  [statusKeys.APPROVAL]: WORKFLOW_STAGE_KEYS.APPROVAL,
  [statusKeys.SUBMISSION]: WORKFLOW_STAGE_KEYS.SUBMISSION
};

/**
 * Compute the route for the Report stage. Mirrors `onRedirectToReportPage` in
 * src/pages/OutputTables/index.jsx:
 *   - Form PF        → /report/pf/us/:workflow/:period
 *   - Annex IV / etc → /report/{fca|esma}/{state}/:workflow/:period, picking
 *                       FCA when the first jurisdiction is UK, ESMA otherwise.
 *
 * Returns '#' when jurisdictions haven't loaded yet (link disabled).
 */
const buildReportPath = (
  workflowId: string,
  periodId: string,
  project: any,
  jurisdictions: any[] | undefined
): string => {
  if (project?.report_type === REPORT_TYPES?.FORM_PF) {
    return `/report/pf/us/${workflowId}/${periodId}`;
  }
  const first = jurisdictions?.[0];
  const state = (first?.reporting_member_state as string | undefined) ?? '';
  if (!state) return '#';
  const reportKind = state === 'UK' ? 'fca' : 'esma';
  return `/report/${reportKind}/${state.toLowerCase()}/${workflowId}/${periodId}`;
};

const buildPath = (
  key: WorkflowStageKey,
  workflowId?: string,
  periodId?: string,
  period?: any,
  project?: any,
  jurisdictions?: any[]
): string => {
  if (!workflowId || !periodId) return '#';
  // Automation / from-template periods route their Source-Files + AIFs entry
  // through the dedicated upload-files page; scratch periods use the Funds
  // page. `template_type === 'sratch'` is the scratch flow (note: backend
  // typo for "scratch" — preserved here to match the API contract).
  const isAutomation = period?.template_type && period.template_type !== 'sratch';
  switch (key) {
    case WORKFLOW_STAGE_KEYS.DASHBOARD:
      // Leading "Dashboard" stage for automation periods — back link to the
      // project's Quarters page. Carries the grouped-view query string when
      // applicable so the user lands on the same list they came from.
      return `/project/quaters/${workflowId}${project?.is_workflow_group ? '?type=grouped' : ''}`;
    case WORKFLOW_STAGE_KEYS.AIFS:
      // Both flows now use the dedicated add-aifs page for managing funds.
      // Previously scratch landed on the Funds page; AddAIFs branches on
      // `template_type === 'sratch'` internally to swap data source and hide
      // the "Previous Period" column.
      return `/project/quaters/${workflowId}/${periodId}/add-aifs`;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      // Both flows use the dedicated upload-files page. UploadFiles branches
      // on `period?.previous_period_id` (`isInitialSetup`) to swap data
      // source, hide drag/expand affordances, hide the Unmap popover item,
      // etc. — see UploadFiles/index.jsx.
      return `/project/quaters/${workflowId}/${periodId}/upload-files`;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      // Jurisdictions lives on a dedicated page. Automation routes nest under
      // /quaters/:id/:periodId; scratch uses the flat /:projectId namespace
      // with the periodId in the query string (matches /funds).
      return isAutomation
        ? `/project/quaters/${workflowId}/${periodId}/jurisdictions`
        : `/project/${workflowId}/jurisdictions?periodId=${periodId}`;
    case WORKFLOW_STAGE_KEYS.EXTRACTION:
      return `/data-extraction/${workflowId}/${periodId}?tab=extraction`;
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION:
      return `/data-extraction/${workflowId}/${periodId}?tab=transformation`;
    case WORKFLOW_STAGE_KEYS.LOOKUP_TABLES:
      return `/data-extraction/${workflowId}/${periodId}?tab=lookup_tables`;
    case WORKFLOW_STAGE_KEYS.OUTPUT_DATA:
      return `/output-tables/${workflowId}/${periodId}`;
    case WORKFLOW_STAGE_KEYS.REPORT:
      // Plain report-viewing page.
      return buildReportPath(workflowId, periodId, project, jurisdictions);
    case WORKFLOW_STAGE_KEYS.APPROVAL: {
      // Approval lives on the report page as a drawer triggered by the
      // `openApprovalDrawer=true` sentinel. Clicking the Approval step
      // navigates to the same pathname with the param set; the report
      // header reads it on mount, opens the drawer, and strips the param.
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (base === '#') return base;
      return base.includes('?')
        ? `${base}&openApprovalDrawer=true`
        : `${base}?openApprovalDrawer=true`;
    }
    case WORKFLOW_STAGE_KEYS.SUBMISSION: {
      // Submission lives on the report page as a modal triggered by the
      // `openSubmitDrawer=true` sentinel. Each report container (ESMA / FCA
      // / FormPF) reads the sentinel and routes into its own submit flow
      // (FCA credentials → SubmitReportModal for ESMA/FCA; direct modal for
      // FormPF). The param is stripped on mount so a refresh doesn't
      // reopen the modal.
      const base = buildReportPath(workflowId, periodId, project, jurisdictions);
      if (base === '#') return base;
      return base.includes('?') ? `${base}&openSubmitDrawer=true` : `${base}?openSubmitDrawer=true`;
    }
    default:
      return '#';
  }
};

/**
 * Pick the right (status, placeholder) deriver pair from Quarters/data.jsx
 * based on the workflow's report type and the period's filing type.
 */
const pickStatusFns = (
  project: any,
  period: any
): {
  getStatusFn: (p: any) => string;
  getPlaceholderFn: (p: any) => string | undefined;
} => {
  if (period?.filing_type === 'PF_TRANS') {
    return { getStatusFn: getStatusForPFTrans, getPlaceholderFn: getPlaceholderForPFTrans };
  }
  if (period?.filing_type === 'PF_FINAL') {
    return { getStatusFn: getStatusForPFFinal, getPlaceholderFn: getPlaceholderForPFFinal };
  }
  if (project?.report_type === REPORT_TYPES?.CONVERSION) {
    return {
      getStatusFn: getStatusForConversionReportType,
      getPlaceholderFn: getPlaceholderForConversionReportType
    };
  }
  if (project?.report_type === REPORT_TYPES?.CUSTOM) {
    return {
      getStatusFn: getStatusForCustomReportType,
      getPlaceholderFn: getPlaceholderForCustomReportType
    };
  }
  return { getStatusFn: getStatus, getPlaceholderFn: getPlaceholder };
};

const buildCountSubLabel = (
  key: WorkflowStageKey,
  period: any,
  labels: Record<WorkflowStageKey, string>
): string | undefined => {
  switch (key) {
    case WORKFLOW_STAGE_KEYS.AIFS:
      return period?.fund_count != null
        ? `${period.fund_count} ${labels[WORKFLOW_STAGE_KEYS.AIFS]}`
        : undefined;
    case WORKFLOW_STAGE_KEYS.JURISDICTIONS:
      return period?.jurisdiction_count != null
        ? `${period.jurisdiction_count} ${labels[WORKFLOW_STAGE_KEYS.JURISDICTIONS].toLowerCase()}`
        : undefined;
    case WORKFLOW_STAGE_KEYS.SOURCE_FILES:
      return period?.file_count != null ? `${period.file_count} files` : undefined;
    case WORKFLOW_STAGE_KEYS.EXTRACTION: {
      // Four visible states:
      //   • not-started → undefined here; caller replaces the placeholder
      //     with "Start Extraction". A pool of not_completed sheets on its
      //     own does NOT count as started — only concrete activity
      //     (successful / failed / warned / in_progress) does.
      //   • in-flight  → "Extracting X/Y" with a spinner (isLoading flag
      //     is set in the map iteration below).
      //   • idle-partial → "Extracted X/Y" (no spinner). Some items finished,
      //     nothing is currently running, but the pool isn't fully drained
      //     (e.g. sheets left in `not_completed` after a partial run).
      //     Previously we always painted "Extracting X/Y" here — the stepper
      //     then spun forever on a queue that wasn't moving.
      //   • finished   → "Finished".
      const stats = period?.extraction_stats;
      if (!stats) return undefined;
      const successful = stats.successful_extractions_number ?? 0;
      const failed = stats.failed_extractions_number ?? 0;
      const warned = stats.warned_extractions_number ?? 0;
      const inProgress = stats.in_progress_extractions_number ?? 0;
      const notCompleted = stats.not_completed_extractions_number ?? 0;
      const hasActivity = successful + failed + warned + inProgress > 0;
      if (!hasActivity) return undefined;
      // "Processed" = anything the pipeline is done with, success OR failure.
      // Failed items count toward the numerator so the fraction reflects
      // pipeline progress ("we're 3/5 of the way through this batch"), not
      // just the healthy-path count. The failure surfacing below still
      // appends "· N failed" and flips the tone to red, so the user sees
      // both the total progress and the failure count at a glance.
      const done = successful + warned + failed;
      const total = done + inProgress + notCompleted;
      if (total === 0) return undefined;
      if (done === total) return 'Finished';
      // Only claim "Extracting" (spinner-on) when something is actually
      // running. `startsWith('Extracting')` in the isLoading gate below
      // then correctly excludes the idle "Extracted" label.
      if (inProgress > 0) return `Extracting ${done}/${total}`;
      return `Extracted ${done}/${total}`;
    }
    case WORKFLOW_STAGE_KEYS.TRANSFORMATION: {
      const stats = period?.transformation_stats;
      if (!stats) return undefined;
      const successful = stats.successful_transformations_number ?? 0;
      const failed = stats.failed_transformations_number ?? 0;
      const inProgress = stats.in_progress_transformation_number ?? 0;
      const notCompleted = stats.not_completed_transformations_number ?? 0;
      const hasActivity = successful + failed + inProgress > 0;
      if (!hasActivity) return undefined;
      // See EXTRACTION above — failed transformations count as processed,
      // and "Transforming" is reserved for the in-flight case only.
      const done = successful + failed;
      const total = done + inProgress + notCompleted;
      if (total === 0) return undefined;
      if (done === total) return 'Finished';
      if (inProgress > 0) return `Transforming ${done}/${total}`;
      return `Transformed ${done}/${total}`;
    }
    default:
      return undefined;
  }
};

/**
 * Returns 8 workflow stages with statuses derived from Quarters/data.jsx.
 *
 * Model: at any point one stage is `current` (the next thing to do); earlier
 * stages are `done`, later stages are `upcoming`. For PF_TRANS / PF_FINAL
 * filings the leading stages (AIFs/Jurisdictions/Source Files/Extraction/
 * Transformation) auto-collapse to `done` because those status fns skip them.
 */
export const useWorkflowStages = (
  args?: UseWorkflowStagesArgs
): { stages: WorkflowStage[]; currentStageIndex: number; isLoading: boolean } => {
  const workflowId = args?.workflowId;
  const periodId = args?.periodId;
  const isLookupEnabled = useFeatureFlag(FEATURE_FLAG_NAMES.LOOKUP_TABLE);

  // Period freshness comes from the chrome-level SignalR sync hook
  // (`useWorkflowChromeStatusSync` in `WorkflowChromeProvider`), which
  // invalidates this query whenever the backend emits any workflow-progress
  // event for the current period. No polling needed.
  const {
    data: period,
    isLoading: isLoadingPeriod,
    refetch: refetchPeriod
  } = usePeriodById({
    periodId,
    queryParams: { enabled: !!periodId }
  }) as { data?: any; isLoading: boolean; refetch: () => void };

  const { data: project, isLoading: isLoadingProject } = useGetWorkflowById({
    id: workflowId,
    queryParams: { enabled: !!workflowId }
  }) as { data?: any; isLoading: boolean };

  const { data: currentUser } = useUserGroupsCurrentUserAdminState() as {
    data?: { role?: string };
  };

  // Needed to compute the Report stage URL (FCA vs ESMA depends on the first
  // jurisdiction's reporting_member_state). Form PF doesn't depend on this.
  const {
    data: jurisdictions,
    isLoading: isLoadingJurisdictions,
    refetch: refetchJurisdictions
  } = useGetJurisdictions({
    periodId,
    queryParams: { enabled: !!periodId }
  }) as { data?: any[]; isLoading: boolean; refetch: () => void };

  // Automation periods use the same endpoints as the ExtractionIssues /
  // TransformationIssues modals — those endpoints refresh instantly on
  // SignalR events, whereas the period record's rolled-up
  // `extraction_stats` / `transformation_stats` can lag by a few seconds
  // after backend transitions. Fetching the live records lets the stepper's
  // "X/Y extracted" sub-label match the modal's numbers exactly.
  const isAutomationPeriod = !!period?.template_type && period.template_type !== 'sratch';
  // Poll the detailed-stats endpoints while the pipeline is in-flight so
  // the stepper still advances even if a SignalR event is missed or the
  // event is emitted at workflow scope (which the strict filter would
  // drop). 5000 ms matches the ExtractionIssuesModal's original polling
  // interval and stops the moment the query response shows nothing left
  // In progress.
  const extractionInFlight = period?.extraction_progress_status === 'In progress';
  const transformationInFlight = period?.transformation_progress_status === 'In progress';
  const { data: extractionRecords, refetch: refetchExtractionStats } =
    useGetDetailedExtractionStatsByPeriodId({
      periodId,
      queryParams: {
        enabled: !!periodId && isAutomationPeriod,
        refetchInterval: extractionInFlight ? 5000 : false,
        statisticsJobPollInterval: extractionInFlight ? 1000 : false,
        staleTime: 0
      }
    }) as { data?: any[]; refetch: () => void };
  const { data: transformationRecords, refetch: refetchTransformationStats } =
    useGetDetailedTransformationStatsByPeriodId({
      periodId,
      queryParams: {
        enabled: !!periodId && isAutomationPeriod,
        refetchInterval: transformationInFlight ? 5000 : false,
        statisticsJobPollInterval: transformationInFlight ? 1000 : false,
        staleTime: 0
      }
    }) as { data?: any[]; refetch: () => void };

  // === Direct SignalR wiring — the step menu subscribes to backend
  // pipeline events and refetches the queries it depends on the moment
  // an event arrives. Mirrors the ExtractionIssuesModal pattern (which
  // calls `refetchIssues()` inside its own SignalR handler) and runs
  // alongside the chrome-level `useWorkflowChromeStatusSync` — this
  // in-hook subscription cuts one round-trip vs. the invalidate-then-
  // refetch dance, so the "X/Y extracted" fraction updates instantly.
  const handleStepperStatusEvent = useCallback(
    (payload?: { workflow_id?: string; period_id?: string }) => {
      if (!periodId) return;
      if (payload?.workflow_id && payload.workflow_id !== workflowId) return;
      if (payload?.period_id && payload.period_id !== periodId) return;
      if (import.meta.env.DEV) {
        console.log('[useWorkflowStages] SignalR match → refetching', {
          periodId,
          workflowId,
          isAutomationPeriod
        });
      }
      // Two-tier trigger — call the query's own `refetch` for an
      // immediate network round-trip, AND invalidate the cache so every
      // mounted consumer (Report container, ExtractionIssuesModal, etc.)
      // reading the same query key also refreshes. `refetchType: 'all'`
      // ensures inactive queries invalidate too, so re-opening a modal
      // after the event sees fresh data straight away.
      refetchPeriod?.();
      // Jurisdiction changes drive the Report stage's URL (FCA vs ESMA)
      // and the AIFs/Jurisdictions sub-labels. Refresh alongside the
      // period so a jurisdiction add/remove immediately propagates to
      // the stepper.
      refetchJurisdictions?.();
      queryClient.invalidateQueries({
        predicate: (query: { queryKey: unknown[] }) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'GET_PERIOD_BY_ID' &&
          query.queryKey[2] === periodId,
        refetchType: 'all'
      });
      if (isAutomationPeriod) {
        refetchExtractionStats?.();
        refetchTransformationStats?.();
        queryClient.invalidateQueries({
          predicate: (query: { queryKey: unknown[] }) => {
            const key = query.queryKey;
            if (!Array.isArray(key)) return false;
            return (
              (key[0] === 'GET_EXTRACTION_STATS' || key[0] === 'GET_TRANSFORMATION_STATS') &&
              key[1] === periodId
            );
          },
          refetchType: 'all'
        });
      }
    },
    [
      periodId,
      workflowId,
      isAutomationPeriod,
      refetchPeriod,
      refetchJurisdictions,
      refetchExtractionStats,
      refetchTransformationStats
    ]
  );

  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED,
    handleStepperStatusEvent
  );
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handleStepperStatusEvent
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_EXTRACTION_STATUS_UPDATED, handleStepperStatusEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_STATUS_UPDATED, handleStepperStatusEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, handleStepperStatusEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, handleStepperStatusEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_FILE_PROCESSING_UPDATED, handleStepperStatusEvent);
  useSignalRListener(SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, handleStepperStatusEvent);
  useSignalRListener(
    SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED,
    handleStepperStatusEvent
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_APPROVAL_STATUS_UPDATED, handleStepperStatusEvent);
  // No dedicated JurisdictionUpdate event exists — `WorkflowPeriodUpdate`
  // is the closest signal (fires on every workflow/period-scoped change,
  // including jurisdiction add/remove). Reuse the same handler so
  // `refetchJurisdictions` runs and the Report stage's URL updates.
  useSignalRListener(SIGNALR_LISTENERS.ON_WORKFLOW_PERIOD_UPDATED, handleStepperStatusEvent);

  // Aggregate the per-sheet detailed records into count buckets that
  // match the shape of `period.extraction_stats` — that way
  // `buildCountSubLabel` can consume either source without branching.
  // Handles both raw-array responses AND `{ data: [...] }` wrappers
  // just in case the interceptor doesn't unwrap.
  const liveExtractionStats = useMemo(() => {
    const raw = extractionRecords as unknown;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { data?: unknown } | undefined)?.data)
        ? (raw as { data: any[] }).data
        : null;
    if (!list) return null;
    let successful = 0;
    let failed = 0;
    let warned = 0;
    let inProgress = 0;
    let notCompleted = 0;
    for (const rec of list) {
      const status = rec?.latest_automation_task?.status;
      if (status === 'Completed') successful += 1;
      else if (status === 'Failed') failed += 1;
      else if (status === 'Warned') warned += 1;
      else if (status === 'In progress') inProgress += 1;
      else notCompleted += 1;
    }
    const result = {
      successful_extractions_number: successful,
      failed_extractions_number: failed,
      warned_extractions_number: warned,
      in_progress_extractions_number: inProgress,
      not_completed_extractions_number: notCompleted
    };
    if (import.meta.env.DEV) {
      console.log('[useWorkflowStages] extraction counts', {
        total: list.length,
        ...result
      });
    }
    return result;
  }, [extractionRecords]);

  const liveTransformationStats = useMemo(() => {
    const raw = transformationRecords as unknown;
    const list = Array.isArray(raw)
      ? raw
      : Array.isArray((raw as { data?: unknown } | undefined)?.data)
        ? (raw as { data: any[] }).data
        : null;
    if (!list) return null;
    let successful = 0;
    let failed = 0;
    let inProgress = 0;
    let notCompleted = 0;
    for (const rec of list) {
      // Transformation-detailed-statistics rows carry their status at the
      // TOP level (`item.status`) with an optional `item.task.status`
      // reflecting the inner backend job. The TransformationIssuesModal
      // reads both — mirror that so we treat a "In progress" task as
      // in-flight regardless of the outer field.
      const outer = rec?.status;
      const inner = rec?.task?.status;
      const status = inner === 'In progress' ? 'In progress' : outer;
      if (status === 'Completed') successful += 1;
      else if (status === 'Failed') failed += 1;
      else if (status === 'In progress') inProgress += 1;
      else notCompleted += 1;
    }
    if (import.meta.env.DEV) {
      console.log('[useWorkflowStages] transformation counts', {
        total: list.length,
        successful,
        failed,
        inProgress,
        notCompleted
      });
    }
    return {
      successful_transformations_number: successful,
      failed_transformations_number: failed,
      in_progress_transformation_number: inProgress,
      not_completed_transformations_number: notCompleted
    };
  }, [transformationRecords]);

  const stages = useMemo<WorkflowStage[]>(() => {
    if (!periodId || !period) return [];

    const stageLabels = buildStageLabels(project?.report_type);
    const { getStatusFn, getPlaceholderFn } = pickStatusFns(project, period);
    const currentStatusKey = getStatusFn(period);
    const placeholder = getPlaceholderFn(period);

    // statusKeys.END means everything is done — no current stage.
    const isAllDone = currentStatusKey === statusKeys.END;
    let currentStageKey = isAllDone ? null : (STATUS_TO_STAGE[currentStatusKey] ?? null);

    // Mirror the ReportColumn's status priority (Quarters/components/Columns/
    // ReportColumn/index.jsx lines 245-266):
    //   1. report_progress_status === 'Completed' → "Generated" (terminal)
    //   2. else report_task?.status === 'In progress' → "Generating report..."
    //   3. else report_task?.status === 'Failed'     → "Failed"
    //
    // Scratch periods (template_type === 'sratch') don't have a separate
    // report-generation task — once report_progress_status leaves
    // 'Not started', the column's default-return branch shows "Report
    // Generated" (green). Treat the same way here.
    const isScratchPeriod = period?.template_type === 'sratch';
    const reportProgress = period?.report_progress_status as string | undefined;
    const reportTaskStatus = period?.report_task?.status as string | undefined;
    // Treat the report as generated when the report task itself reports
    // Completed too — the file is built and viewable, even if the overall
    // report_progress_status is still "In progress" (e.g. comments unresolved).
    const isReportGenerated =
      reportProgress === 'Completed' ||
      reportTaskStatus === 'Completed' ||
      (isScratchPeriod && !!reportProgress && reportProgress !== 'Not started');
    const isReportInProgress = !isReportGenerated && reportTaskStatus === 'In progress';
    const isReportFailed = !isReportGenerated && reportTaskStatus === 'Failed';

    // The status fns stick at `REPORT` whenever the report file isn't
    // generated OR comments aren't fully resolved — that maps to the
    // OUTPUT_DATA stage by default. Refine the cursor placement based on
    // what's actually happening on the report side:
    //   - report file built → cursor → APPROVAL (resolve-comments lives there)
    //   - report task in flight / failed → cursor → REPORT (surface progress)
    //   - report not started yet → leave cursor on OUTPUT_DATA (Generate Report)
    if (
      currentStatusKey === statusKeys.REPORT &&
      currentStageKey === WORKFLOW_STAGE_KEYS.OUTPUT_DATA
    ) {
      if (isReportGenerated) {
        currentStageKey = WORKFLOW_STAGE_KEYS.APPROVAL;
      } else if (isReportInProgress || isReportFailed) {
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      } else if (project?.report_type === REPORT_TYPES?.CONVERSION) {
        // Conversion's ordered list doesn't include OUTPUT_DATA — its
        // sequence is … Jurisdictions → Report → Approval → Submission.
        // Snap the cursor onto the visible REPORT stage; otherwise
        // `filteredCurrentKey` falls back to the first non-Dashboard
        // stage (Source Files) and the earlier chips render as current
        // even though setup is complete.
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      } else if (period?.transformation_progress_status === 'Completed') {
        // Once transformation is done, the intermediate Lookup / Output
        // Data steps are auto-completed by the backend as the report is
        // generated. Move the cursor straight to REPORT so the user isn't
        // stuck on Output Data waiting for something they can't act on.
        currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
      }
    }

    // Live-stats often flip to "Finished" for a stage seconds before
    // the aggregate `*_progress_status` field on the period record
    // catches up. If we leave `currentStageKey` on that stage, the
    // linear progression turns EVERY following stage into `upcoming`
    // with no sub-label (renders as em-dash). Advance the cursor to
    // the next real stage so the follow-on chip becomes the proper
    // current step.
    const extractionCountsSayDone = liveExtractionStats
      ? (() => {
          const done =
            liveExtractionStats.successful_extractions_number +
            liveExtractionStats.warned_extractions_number;
          const total =
            done +
            liveExtractionStats.failed_extractions_number +
            liveExtractionStats.in_progress_extractions_number +
            liveExtractionStats.not_completed_extractions_number;
          return total > 0 && done === total;
        })()
      : false;
    const transformationCountsSayDone = liveTransformationStats
      ? (() => {
          const done = liveTransformationStats.successful_transformations_number;
          const total =
            done +
            liveTransformationStats.failed_transformations_number +
            liveTransformationStats.in_progress_transformation_number +
            liveTransformationStats.not_completed_transformations_number;
          return total > 0 && done === total;
        })()
      : false;
    if (currentStageKey === WORKFLOW_STAGE_KEYS.EXTRACTION && extractionCountsSayDone) {
      currentStageKey = WORKFLOW_STAGE_KEYS.TRANSFORMATION;
    }
    if (currentStageKey === WORKFLOW_STAGE_KEYS.TRANSFORMATION && transformationCountsSayDone) {
      currentStageKey = WORKFLOW_STAGE_KEYS.REPORT;
    }

    const isInProgressVariant =
      currentStatusKey === statusKeys.EXTRACTION_IN_PROGRESS ||
      currentStatusKey === statusKeys.TRANSFORMATION_IN_PROGRESS;
    // `DASHBOARD` renders when EITHER the current period is automation /
    // from-template OR the workflow has at least two periods. Both cases
    // benefit from a quick back link to the Quarters page — automation
    // periods because they're nested under /quaters/:id, multi-period
    // workflows because the user may want to jump between siblings.
    const isAutomation = period?.template_type && period.template_type !== 'sratch';
    const hasMultiplePeriods = (project?.periods?.length ?? 0) >= 2;
    // Also render Dashboard once the report has been generated — at that
    // point the user is in review territory and benefits from a quick way
    // back to the project's Quarters page.
    const showDashboard = isAutomation || hasMultiplePeriods || isReportGenerated;

    let orderedKeys = isLookupEnabled
      ? STAGE_ORDER
      : STAGE_ORDER.filter(k => k !== WORKFLOW_STAGE_KEYS.LOOKUP_TABLES);
    if (!showDashboard) {
      orderedKeys = orderedKeys.filter(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD);
    }
    // Conversion report type uses a distinct visual sequence:
    // Dashboard → Source Files → AIFs → Jurisdictions → Report → Approval →
    // Submission. Diverges from STAGE_ORDER (which puts AIFs / Jurisdictions
    // before Source Files); aligns with getStatusForConversionReportType in
    // Quarters/data.jsx, which already checks FILES first → AIF → JURISDICTION
    // → REPORT → APPROVAL → SUBMISSION. Hard-coded order rather than a filter
    // so the divergent sequence is explicit.
    if (project?.report_type === REPORT_TYPES?.CONVERSION) {
      // Conversion always leads with Dashboard. After a Jurisdictions save we
      // route the user back to the Quarters page, so the back-link belongs in
      // the stepper regardless of automation / multi-period / report-generated
      // gates that apply to other report types.
      orderedKeys = [
        WORKFLOW_STAGE_KEYS.DASHBOARD,
        WORKFLOW_STAGE_KEYS.SOURCE_FILES,
        WORKFLOW_STAGE_KEYS.AIFS,
        WORKFLOW_STAGE_KEYS.JURISDICTIONS,
        WORKFLOW_STAGE_KEYS.REPORT,
        WORKFLOW_STAGE_KEYS.APPROVAL,
        WORKFLOW_STAGE_KEYS.SUBMISSION
      ];
    }
    if (currentUser?.role === 'Approver') {
      const allowed = new Set<WorkflowStageKey>([
        WORKFLOW_STAGE_KEYS.OUTPUT_DATA,
        WORKFLOW_STAGE_KEYS.REPORT
      ]);
      orderedKeys = orderedKeys.filter(k => allowed.has(k));
    }

    // If the workflow's actual current stage was filtered out (e.g. an Approver
    // looking at a workflow whose cursor is on AIFs), pin the first remaining
    // stage as "current" so the stepper isn't entirely blank or stuck on a
    // hidden status.
    const filteredCurrentKey =
      currentStageKey && orderedKeys.includes(currentStageKey as WorkflowStageKey)
        ? (currentStageKey as WorkflowStageKey)
        : (orderedKeys.find(k => k !== WORKFLOW_STAGE_KEYS.DASHBOARD) ?? null);
    let passedCurrent = false;

    return orderedKeys.map(key => {
      let status: WorkflowStageStatus;
      let tone: WorkflowStageTone;

      // Dashboard is a back-link, not a real workflow stage — always render
      // it as `done`/success so it sits left-of-current without breaking the
      // linear progression model.
      if (key === WORKFLOW_STAGE_KEYS.DASHBOARD) {
        return {
          key,
          label: stageLabels[key],
          path: buildPath(key, workflowId, periodId, period, project, jurisdictions),
          status: 'done' as WorkflowStageStatus,
          tone: 'success' as WorkflowStageTone,
          subLabel: undefined
        };
      }

      if (isAllDone) {
        status = 'done';
        tone = 'success';
      } else if (key === filteredCurrentKey) {
        status = 'current';
        tone =
          currentStatusKey === statusKeys.APPROVAL
            ? 'warning'
            : isInProgressVariant
              ? 'info'
              : 'info';
        passedCurrent = true;
      } else if (!passedCurrent) {
        status = 'done';
        tone = 'success';
      } else {
        status = 'upcoming';
        tone = 'neutral';
      }

      // Prefer the live detailed-stats aggregates on automation periods —
      // the period record's rolled-up stats can lag behind SignalR events.
      const periodForCounts =
        liveExtractionStats || liveTransformationStats
          ? {
              ...period,
              extraction_stats: liveExtractionStats ?? period.extraction_stats,
              transformation_stats: liveTransformationStats ?? period.transformation_stats
            }
          : period;
      const countLabel = buildCountSubLabel(key, periodForCounts, stageLabels);
      // The REPORT stage represents viewing the generated report — once the
      // user is on it, no actionable sub-label is needed. Skip the "Resolve
      // comments first" / "Generate Report" placeholders for this stage.
      const useStatusPlaceholder =
        key === filteredCurrentKey && placeholder && key !== WORKFLOW_STAGE_KEYS.REPORT;
      // "Approved" reads more accurately than "Finished" on the Approval
      // chip once the phase is done — matches the domain language everywhere
      // else in the reviewer flow (Approved / Pending status dots, "Approve
      // AIF Report" button, etc.).
      const doneLabel = key === WORKFLOW_STAGE_KEYS.APPROVAL ? 'Approved' : 'Finished';
      let subLabel: string | undefined = useStatusPlaceholder
        ? placeholder
        : status === 'done'
          ? (countLabel ?? doneLabel)
          : countLabel;

      // When EXTRACTION or TRANSFORMATION is the current stage on an
      // automation / from-template period, surface the "X/Y extracted" /
      // "X/Y transformed" fraction instead of the generic "in progress"
      // placeholder. Scratch periods keep the original placeholder
      // because their stats are driven by user-created transformations,
      // not a bulk pipeline where a running total is meaningful.
      // Reuses `isAutomation` computed once at the top of the useMemo.
      if (
        isAutomation &&
        status === 'current' &&
        countLabel &&
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION)
      ) {
        subLabel = countLabel;
      }

      // Live-stats can flip to "Finished" before the aggregate
      // `extraction_progress_status` / `transformation_progress_status`
      // field catches up on the backend — during that window the stage
      // reads as `current` (blue) while its sub-label already says
      // "Finished". Force it to done+success so the tone matches the
      // reality visible in the sub-label.
      if (
        isAutomation &&
        status === 'current' &&
        countLabel === 'Finished' &&
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION)
      ) {
        status = 'done';
        tone = 'success';
      }

      // Failure surfacing — when the pipeline has failed sheets, append
      // "· N failed" to the sub-label and flip the tone to `error` (red)
      // so the user can spot problems at a glance without opening the
      // Issues drawer. Runs regardless of status so failures stay
      // visible on both `current` and `done` chips.
      if (isAutomation && key === WORKFLOW_STAGE_KEYS.EXTRACTION) {
        const failed = periodForCounts?.extraction_stats?.failed_extractions_number ?? 0;
        if (failed > 0) {
          subLabel = subLabel ? `${subLabel} · ${failed} failed` : `${failed} failed`;
          tone = 'error';
        }
      } else if (isAutomation && key === WORKFLOW_STAGE_KEYS.TRANSFORMATION) {
        const failed = periodForCounts?.transformation_stats?.failed_transformations_number ?? 0;
        if (failed > 0) {
          subLabel = subLabel ? `${subLabel} · ${failed} failed` : `${failed} failed`;
          tone = 'error';
        }
      }

      // The placeholder fns in Quarters/data.jsx return hardcoded "Add Funds"
      // / "Add Jurisdictions" strings for the AIFS / JURISDICTIONS stages —
      // those don't follow the per-report-type naming (e.g. Form PF's
      // "Fund Info" label, Custom's "Assets"). Swap to `Add ${stageLabel}` so
      // the sub-label reads "Add Fund Info" for Form PF instead of
      // "Add Jurisdictions". Only fires when the current placeholder is the
      // generic noun — other placeholder strings (Upload Files, Create
      // Extraction, etc.) pass through untouched.
      if (
        useStatusPlaceholder &&
        (key === WORKFLOW_STAGE_KEYS.JURISDICTIONS || key === WORKFLOW_STAGE_KEYS.AIFS)
      ) {
        subLabel = `Add ${stageLabels[key]}`;
      }

      // Nicer copy for the EXTRACTION / TRANSFORMATION not-started states.
      // The generic placeholders read "Create Extraction" / "Create
      // Transformation"; the stepper prefers action-shaped labels so the
      // user reads it as "here's the button, click me".
      if (key === filteredCurrentKey && key === WORKFLOW_STAGE_KEYS.EXTRACTION && !countLabel) {
        subLabel = 'Start Extraction';
      } else if (
        key === filteredCurrentKey &&
        key === WORKFLOW_STAGE_KEYS.TRANSFORMATION &&
        !countLabel
      ) {
        subLabel = 'Start Transformation';
      }

      // Spinner beside the sub-label whenever the pipeline is mid-flight
      // (the countLabel starts with "Extracting" / "Transforming"). Rendered
      // by StepItem — pure UI concern, doesn't affect the label text.
      const isLoading =
        (key === WORKFLOW_STAGE_KEYS.EXTRACTION || key === WORKFLOW_STAGE_KEYS.TRANSFORMATION) &&
        typeof countLabel === 'string' &&
        (countLabel.startsWith('Extracting') || countLabel.startsWith('Transforming'));

      // Mirror ReportColumn's report_task handling on the REPORT stage:
      // surface "Generating report..." / "Failed" sub-labels and tones when
      // the underlying report task is mid-flight or failed. When the task is
      // Completed (report file built), promote the stage to done/success
      // even if the overall phase is still "In progress" (unresolved
      // comments etc.) — the report-generation work itself is finished.
      if (key === WORKFLOW_STAGE_KEYS.REPORT) {
        if (isReportInProgress) {
          subLabel = 'Generating report...';
          tone = 'info';
        } else if (isReportFailed) {
          subLabel = 'Failed';
          tone = 'warning';
        } else if (isReportGenerated) {
          subLabel = 'Generated';
          status = 'done';
          tone = 'success';
        } else if (status === 'current') {
          // Report is the next actionable step (previous stages done,
          // no report task yet). Show a clickable "Generate Report"
          // affordance in the info tone so the user knows what to do.
          subLabel = 'Generate Report';
          tone = 'info';
        }
      }

      // When the cursor lands on APPROVAL purely because the report file is
      // generated (status fn still at REPORT due to comments), the inherited
      // placeholder text is "Resolve comments first" — that's REPORT-phase
      // advice on an APPROVAL-phase stage. Replace it with the approval
      // placeholder so the sub-label fits the stage the user actually sees.
      if (
        key === WORKFLOW_STAGE_KEYS.APPROVAL &&
        key === filteredCurrentKey &&
        isReportGenerated &&
        currentStatusKey === statusKeys.REPORT
      ) {
        subLabel = 'Approval Pending';
      }

      // Report / Approval / Submission are gated on actual workflow progress
      // — mirrors the Quarters table's ReportColumn / SubmissionColumn rules.
      // When the workflow hasn't reached the corresponding phase yet, fall
      // through to path = '#' so the stepper renders the button disabled
      // instead of letting the user click and land on a non-functional page.
      let path = buildPath(key, workflowId, periodId, period, project, jurisdictions);
      if (key === WORKFLOW_STAGE_KEYS.REPORT) {
        // Viewing the generated report is unlocked once the report exists
        // (covered by `isReportGenerated`, which also handles scratch
        // periods that never get a separate report_task) OR the workflow
        // has advanced past report generation to APPROVAL / SUBMISSION / END.
        // Also unlocked once transformation is complete — Lookup / Output
        // Data auto-progress once the pipeline finishes, and the user
        // wants a direct path to the report page from there.
        const isConversionReady =
          project?.report_type === REPORT_TYPES?.CONVERSION &&
          currentStatusKey === statusKeys.REPORT;
        const isReportActive =
          isReportGenerated ||
          currentStatusKey === statusKeys.APPROVAL ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END ||
          period?.transformation_progress_status === 'Completed' ||
          isConversionReady;
        if (!isReportActive) path = '#';
      }
      if (key === WORKFLOW_STAGE_KEYS.APPROVAL) {
        const approveProgress = (period?.approve_progress_status as string | undefined) ?? '';
        // Once the report file exists the user can already assign approvers,
        // even if comments are still being resolved. The status fn sticks at
        // REPORT in that case (comments check), so we explicitly unlock the
        // Approval path via `isReportGenerated`.
        const isApprovalActive =
          approveProgress === 'Completed' ||
          isReportGenerated ||
          currentStatusKey === statusKeys.APPROVAL ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END;
        if (!isApprovalActive) path = '#';
      }
      if (key === WORKFLOW_STAGE_KEYS.SUBMISSION) {
        const submitProgress = (period?.submit_progress_status as string | undefined) ?? '';
        const isSubmissionActive =
          submitProgress === 'Completed' ||
          currentStatusKey === statusKeys.SUBMISSION ||
          currentStatusKey === statusKeys.END;
        if (!isSubmissionActive) path = '#';
      }

      // Report stage label stays as plain "Report" regardless of generation
      // state; the per-stage status / placeholder already conveys whether
      // the report still needs to be generated.
      const label = stageLabels[key];

      // One-click shortcuts. Only automation / from-template periods can
      // reuse the previous period's setup. Each stage exposes a single
      // shortcut kind; the stepper owns the actual mutation.
      let action: WorkflowStageAction | undefined;
      const isAutomationPeriod = period?.template_type && period.template_type !== 'sratch';
      if (
        key === WORKFLOW_STAGE_KEYS.JURISDICTIONS &&
        isAutomationPeriod &&
        !period?.jurisdiction_count &&
        period?.jurisdiction_validation_progress_status !== 'Completed'
      ) {
        action = {
          kind: 'copy-jurisdictions',
          label: `Copy ${stageLabels[key].toLowerCase()} from previous period`
        };
      } else if (
        key === WORKFLOW_STAGE_KEYS.EXTRACTION &&
        isAutomationPeriod &&
        period?.extraction_progress_status === 'Not started'
      ) {
        action = {
          kind: 'automate-extraction',
          label: 'Run automated extraction from previous period'
        };
      } else if (
        key === WORKFLOW_STAGE_KEYS.TRANSFORMATION &&
        isAutomationPeriod &&
        period?.transformation_progress_status === 'Not started'
      ) {
        action = {
          kind: 'automate-transformation',
          label: 'Run automated transformation from previous period'
        };
      }

      return {
        key,
        label,
        path,
        status,
        tone,
        subLabel,
        action,
        isLoading
      };
    });
  }, [
    periodId,
    workflowId,
    period,
    project,
    jurisdictions,
    isLookupEnabled,
    currentUser?.role,
    liveExtractionStats,
    liveTransformationStats
  ]);

  // Index of the current stage (or last done if everything is finished) — used
  // by the back/next buttons in the WorkflowStepper.
  const currentStageIndex = useMemo(() => {
    if (stages.length === 0) return -1;
    const idx = stages.findIndex(s => s.status === 'current');
    if (idx >= 0) return idx;
    // No 'current' → all done; treat last stage as the cursor.
    return stages.length - 1;
  }, [stages]);

  return {
    stages,
    currentStageIndex,
    isLoading: isLoadingPeriod || isLoadingProject || isLoadingJurisdictions
  };
};
